import React, { createContext, useContext, useState, ReactNode } from 'react';
import noop from 'utils/noop';
import { usePanelContext } from './Panel';
import { useUploadAssetsContext } from './UploadAssetsContext';
import { PanelView } from '../types';

interface CloseModalOverlayContextType {
  showOverlay: boolean;
  setShowOverlay: (showOverlay: boolean) => void;
  onCloseModal: (isClose?: boolean) => void;
  onConfirm: () => void;
  panelView: PanelView;
}

export const CloseModalOverlayContext = createContext<CloseModalOverlayContextType>({
  showOverlay: false,
  setShowOverlay: noop,
  onCloseModal: noop,
  panelView: PanelView.List,
  onConfirm: noop,
});

export const useCloseModalOverlayContext = () => useContext(CloseModalOverlayContext);

interface CloseModalOverlayProviderProps {
  children: ReactNode;
}

export const CloseModalProvider: React.FC<CloseModalOverlayProviderProps> = ({ children }) => {
  const [isCloseModal, setIsCloseModal] = useState<boolean>(false);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const { onCancel, panelView, setPanelView, setPanelIntent, initialIntent } = usePanelContext();
  const { pendingImages } = useUploadAssetsContext();

  const abortUploads = () => {
    pendingImages.forEach((image) => {
      image.abortUpload?.();
    });
  };

  const onConfirm = () => {
    abortUploads();
    setShowOverlay(false);
    if (panelView !== PanelView.List && !isCloseModal) {
      setPanelView(PanelView.List);
      setPanelIntent(initialIntent);
      return;
    }
    onCancel();
  };

  const onCloseModal = (isClose = false) => {
    if (panelView === PanelView.Upload && pendingImages.length > 0) {
      setShowOverlay(true);
      setIsCloseModal(isClose);
      return;
    }

    if (panelView !== PanelView.List && !isClose) {
      setPanelView(PanelView.List);
      setPanelIntent(initialIntent);
      return;
    }
    onCancel();
  };

  const value: CloseModalOverlayContextType = {
    showOverlay,
    setShowOverlay,
    onCloseModal,
    onConfirm,
    panelView,
  };

  return (
    <CloseModalOverlayContext.Provider value={value}>{children}</CloseModalOverlayContext.Provider>
  );
};
